import React, { createContext, useContext, useEffect, useState } from "react";
import translations from "./translations";

export const LanguageContext = createContext(null);

const STORAGE_KEY = "mk-lang";
const SUPPORTED = ["en", "id"];

// Resolution order: (a) saved preference in localStorage, then
// (b) automatic detection from the browser language ("id"/"id-ID" -> "id",
// anything else -> "en").
function resolveInitialLanguage() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED.includes(saved)) {
      return saved;
    }
  } catch (_) {
    // localStorage unavailable (e.g. private mode); fall through to detection.
  }
  const nav = window.navigator || {};
  const preferred = (nav.languages && nav.languages[0]) || nav.language || "";
  return preferred.toLowerCase().startsWith("id") ? "id" : "en";
}

function lookup(dict, key) {
  return key
    .split(".")
    .reduce((node, part) => (node ? node[part] : undefined), dict);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(resolveInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (next) => {
    if (!SUPPORTED.includes(next)) {
      return;
    }
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {
      // Persistence is best-effort; the in-memory choice still applies.
    }
  };

  // t("nav.home") -> translated string. Falls back to English, then to the
  // key itself, so a missing entry never renders blank.
  const t = (key) => {
    const value = lookup(translations[lang], key);
    if (typeof value === "string") {
      return value;
    }
    const fallback = lookup(translations.en, key);
    return typeof fallback === "string" ? fallback : key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
